import { useMutation, useQuery } from '@tanstack/react-query'
import type { Talent } from '@/types/resource.types.ts'
import type { FiltersApiConfig } from '@/types/filter.types.ts'
import type { PaginationOptions } from '@/types/api.types.ts'
import type { ConsultantFilters } from '@/utils/apiUtils.ts'
import type { FiltersConfigParams } from '@/services/api/resources.ts'
import {
  addTalentFavorite,
  fetchCandidates,
  fetchCandidatesFiltersConfig,
  fetchTalent,
  fetchTalentNetwork,
  removeTalentFavorite,
} from '@/services/api/resources.ts'
import { RESOURCES } from '@/constants/apiConstants.ts'

export const resourcesKeys = {
  all: ['resources'] as const,
  candidates: (filters?: Record<string, unknown>) =>
    [...resourcesKeys.all, 'candidates', filters ?? {}] as const,
  candidatesFiltersConfig: (filters?: FiltersConfigParams) =>
    [
      ...resourcesKeys.all,
      'candidates',
      RESOURCES.FILTERS_CONFIG,
      filters ?? {},
    ] as const,
  talent: (talentId: Talent['unified_identity_id']) =>
    [...resourcesKeys.all, 'talent', talentId] as const,
  talentNetwork: (talentId: Talent['unified_identity_id']) =>
    [...resourcesKeys.all, 'talentNetwork', talentId] as const,
}

export const useCandidates = (
  { offset, limit }: PaginationOptions = {},
  query?: string,
  sort?: string,
  filters?: Omit<ConsultantFilters, 'rtype'>,
) =>
  useQuery({
    queryKey: resourcesKeys.candidates({
      offset,
      limit,
      query,
      sort,
      ...filters,
    }),
    queryFn: () => fetchCandidates({ offset, limit }, query, sort, filters),
  })

export const useCandidatesFiltersConfig = (filters?: FiltersConfigParams) =>
  useQuery<FiltersApiConfig>({
    queryKey: resourcesKeys.candidatesFiltersConfig(filters),
    queryFn: () => fetchCandidatesFiltersConfig(filters),
  })

export const useTalent = (talentId: Talent['unified_identity_id']) =>
  useQuery({
    queryKey: resourcesKeys.talent(talentId),
    queryFn: () => fetchTalent(talentId),
    enabled: !!talentId,
  })

export const useTalentNetwork = (talentId: Talent['unified_identity_id']) =>
  useQuery({
    queryKey: resourcesKeys.talentNetwork(talentId),
    queryFn: () => fetchTalentNetwork(talentId),
    enabled: !!talentId,
  })

export const useAddTalentFavorite = () =>
  useMutation({ mutationFn: addTalentFavorite })

export const useRemoveTalentFavorite = () =>
  useMutation({ mutationFn: removeTalentFavorite })
